import { ref, computed, watch } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import moment from 'moment-timezone'
import store from '@/store'
import { useUtils as useI18nUtils } from "@core/libs/i18n";
import { showOrigin } from '@/views/helpdesk/catalogConfig'
import ability from '@/libs/acl/ability'
import { debounce } from 'lodash'

export default function useList(catalog = 'siniestro') {
  const { t } = useI18nUtils();
  const trans = {
    "messages.error.server_error": t("messages.error.server_error"),
  };
  const refListTable = ref(null)

  const toast = useToast()

  // Solo mostramos los subtipos (children_recursive): code, name y fechas
  const tableColumns = [
    { label: 'Código', key: 'code', sortable: true },
    { label: 'Nombre', key: 'name', sortable: true },
    ...(showOrigin(catalog) ? [{ label: 'Origen', key: '__root', sortable: true, formatter: value => (value && value.name) || '' }] : []),
    { label: 'Fecha De Creación', key: 'created_at', sortable: true, formatter: 'formatDateAssigned' },
    { label: 'Fecha De Actualización', key: 'updated_at', sortable: true, formatter: 'formatDateAssigned' },
  ]

  if (ability.can('update', 'HelpdeskSinisterType') == true || ability.can('delete', 'HelpdeskSinisterType') == true) {
    tableColumns.splice(0, 0, { label: 'actions.name', key: 'actions' })
  }

  const perPage = ref(10)
  const totalList = ref(0)
  const currentPage = ref(1)
  const perPageOptions = [10, 25, 50, 100]
  const searchQuery = ref('')
  const sortBy = ref('id')
  const isSortDirDesc = ref(true)

  const dataMeta = computed(() => {
    const localItemsCount = refListTable.value ? refListTable.value.localItems.length : 0
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalList.value,
    }
  })

  // Aplana recursivamente los subtipos (children_recursive) de cada raíz.
  const flattenChildren = roots => {
    const out = []
    const walk = (list, root) => {
      (list || []).forEach(node => {
        out.push({ ...node, __root: root })
      })
    }
    ;(roots || []).forEach(root => walk(root.children_recursive, root))
    return out
  }

  // ─────────────────────────────────────────────────────────────
  // El backend responde en DOS shapes:
  //  • Sin q   → árbol: raíces con children_recursive (nivel 1 → 2 → 3).
  //  • Con q   → lista PLANA de nodos que hacen match (cualquier nivel),
  //              sin children_recursive.
  // Esta tabla muestra TIPOS (nivel 2), así que:
  //  • árbol → flatten normal (hijos de cada raíz)
  //  • plano → filtramos level === 2 y resolvemos __root (nivel 1) por parent_id
  // ─────────────────────────────────────────────────────────────
  const normalizeRows = (data) => {
    const list = data || []
    const isTree = list.some(n => Array.isArray(n.children_recursive))
    if (isTree) return flattenChildren(list)

    // Resultado plano de búsqueda
    const byId = new Map(list.map(n => [n.id, n]))
    return list
      .filter(n => n.level === 2)
      .map(n => ({ ...n, __root: byId.get(n.parent_id) || null }))
  }

  const allRows = ref([])
  const loaded = ref(false)

  const loadAll = () => {
    const params = {
      catalog: [catalog],
      q: searchQuery.value || '',
      max: 1000,
      'page[n]': 1,
      sortBy: 'id',
      sortDesc: true,
    }
    // eslint-disable-next-line no-console
    console.log('getAllFailureCategories PARAMS >>>', params)
    return store.dispatch('helpdesk/getAllFailureCategories', params).then(response => {
      // eslint-disable-next-line no-console
      console.log('getAllFailureCategories RESPONSE >>>', response)
      allRows.value = normalizeRows(response.data)
      loaded.value = true
    })
  }

  const sortValue = (row, key) => {
    if (key === '__root') return ((row.__root && row.__root.name) || '').toLowerCase()
    const v = row[key]
    if (key === 'created_at' || key === 'updated_at') return new Date(v || 0).getTime()
    return (v == null ? '' : v).toString().toLowerCase()
  }

  // Ordena y pagina en cliente (la búsqueda ya la hizo el servidor con q).
  const applyView = (callback) => {
    let rows = allRows.value.slice()

    const key = sortBy.value
    if (key && key !== 'id') {
      rows.sort((a, b) => {
        const av = sortValue(a, key)
        const bv = sortValue(b, key)
        if (av < bv) return isSortDirDesc.value ? 1 : -1
        if (av > bv) return isSortDirDesc.value ? -1 : 1
        return 0
      })
    }

    totalList.value = rows.length

    const start = (currentPage.value - 1) * perPage.value
    callback(rows.slice(start, start + perPage.value))
  }

  const fetchList = (ctx, callback) => {
    const run = () => applyView(callback)
    if (loaded.value) {
      run()
      return
    }
    loadAll()
      .then(run)
      .catch(error => {
        console.error('getAllFailureCategories ERROR >>>', error)
        toast({
          component: ToastificationContent,
          props: {
            title: trans['messages.error.server_error'],
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
        callback([])
      })
  }

  const formatDateAssigned = value => {
    if (!value) return ''
    return moment(value).format('DD/MM/YYYY HH:mm:ss')
  }

  const refreshTable = () => {
    if (refListTable.value) refListTable.value.refresh()
  }

  // Recarga desde el servidor (usar tras agregar/editar/eliminar o al buscar).
  const refetchData = () => {
    loaded.value = false
    currentPage.value = 1
    refreshTable()
  }

  // Dispara la búsqueda al servidor AHORA (p. ej. con Enter).
  const searchNow = () => {
    loaded.value = false
    currentPage.value = 1
    refreshTable()
  }

  // Cambios de página / tamaño → solo re-paginar en cliente (sin servidor).
  watch([currentPage, perPage], () => {
    refreshTable()
  })

  // Al dejar de teclear → buscar en el servidor con q.
  watch([searchQuery], debounce(() => {
    searchNow()
  }, 500))

  return {
    tableColumns,
    perPage,
    currentPage,
    totalList,
    fetchList,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refListTable,
    formatDateAssigned,
    refetchData,
    searchNow,
  }
}